import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FoodSelect, apiErr, fmtQ } from "@/merenda/common";
import { MenuImportRows } from "@/merenda/MenuImportRows";

const MATCH = { EXATO: "bg-green-100 text-green-700", SUGERIDO: "bg-amber-100 text-amber-800", NAO_ENCONTRADO: "bg-red-100 text-red-700" };

export const ImportReview = ({ imp, meta, foods, scopeParams, onClose, onDone, onFoodsChanged }) => {
  const r = imp.result || {};
  const [rows, setRows] = useState(r.rows || []);
  const [hfoods, setHfoods] = useState(r.foods || []);
  const [menu, setMenu] = useState(r.menu || []);
  const [busy, setBusy] = useState(false);
  const [saveRecipes, setSaveRecipes] = useState(true);
  const mealLb = Object.fromEntries(meta.meals);
  const links = r.links || {};
  const linked = (name) => Object.keys(hfoods.find((f) => f.name === name)?.days || {}).filter((d) => (links[d]?.[name] || []).length).length;

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/merenda/imports/${imp.id}/confirm`, { rows, foods: hfoods, menu, links, save_recipes: saveRecipes }, { params: scopeParams });
      toast.success(imp.kind === "MENU" ? "Cardápio gravado" : "Histórico gravado (sem movimentar o estoque)"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const discard = async () => { await api.post(`/merenda/imports/${imp.id}/discard`, {}, { params: scopeParams }); onDone(); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="import-review-dialog">
        <DialogHeader><DialogTitle>Conferência — {imp.kind === "MENU" ? "Cardápio" : "Mapa histórico"} {imp.ym}</DialogTitle></DialogHeader>
        {(r.illegible || []).length > 0 && <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid="import-illegible">Não identificado / ilegível: {r.illegible.join("; ")}</div>}
        {imp.kind === "MENU" ? (
          <MenuImportRows rows={rows} setRows={setRows} foods={foods} mealLb={mealLb} scopeParams={scopeParams} onFoodsChanged={onFoodsChanged} />
        ) : (
          <>
            {r.mapa && <p className="text-xs text-slate-600">Mapa de Controle lido: matrículas {r.mapa.matriculas ?? "—"}, média de atendimentos {r.mapa.media_atendimentos ?? "—"}, dias {r.mapa.dias ?? "—"}, refeições {r.mapa.refeicoes ?? "—"}.</p>}
            <h4 className="font-semibold text-slate-800">Gêneros ({hfoods.length})</h4>
            <Table>
              <TableHeader><TableRow><TableHead>Nome no documento</TableHead><TableHead>Associar a</TableHead><TableHead className="text-right">Total doc.</TableHead><TableHead className="text-right">Soma dias</TableHead><TableHead>Dias c/ refeição identificada</TableHead></TableRow></TableHeader>
              <TableBody>{hfoods.map((f, i) => (
                <TableRow key={i} data-testid={`import-food-${i}`}>
                  <TableCell>{f.name} <Badge className={MATCH[f.match]}>{f.match === "EXATO" ? "ok" : f.match === "SUGERIDO" ? "sugerido" : "não encontrado"}</Badge></TableCell>
                  <TableCell className="min-w-[220px]"><FoodSelect foods={foods} value={f.food_id} onChange={(v) => setHfoods(hfoods.map((y, j) => (j === i ? { ...y, food_id: v || null } : y)))} testid={`import-food-select-${i}`} /></TableCell>
                  <TableCell className="text-right">{fmtQ(f.total)}</TableCell>
                  <TableCell className={`text-right ${f.total != null && Math.abs(f.total - f.sum_days) > 0.01 ? "font-bold text-red-600" : ""}`}>{fmtQ(f.sum_days)}</TableCell>
                  <TableCell>{linked(f.name)} / {Object.keys(f.days).length}</TableCell>
                </TableRow>))}</TableBody>
            </Table>
            <p className="text-xs text-slate-500">Soma em vermelho = diverge do total do documento (confira). Dias sem refeição identificada não entram na média; alimentos sem nenhuma relação pedirão referência conferida.</p>
            <h4 className="font-semibold text-slate-800">Comensais por dia e refeição ({menu.length})</h4>
            <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">{menu.map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-xs"><span className="w-28">Dia {m.day} · {mealLb[m.meal]}</span>
                <Input className="h-8 w-20" value={m.comensais ?? ""} onChange={(e) => setMenu(menu.map((y, j) => (j === i ? { ...y, comensais: e.target.value === "" ? null : Number(e.target.value) } : y)))} data-testid={`import-comensais-${i}`} /></div>))}</div>
          </>
        )}
        <DialogFooter className="gap-2 sm:items-center">
          {imp.kind === "MENU" && <label className="mr-auto flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={saveRecipes} onChange={(e) => setSaveRecipes(e.target.checked)} data-testid="import-save-recipes" />Guardar receitas para os próximos meses</label>}
          <Button variant="ghost" className="text-rose-600" onClick={discard} data-testid="import-discard">Descartar</Button>
          <Button className="bg-orange-500 hover:bg-orange-600" disabled={busy} onClick={confirm} data-testid="import-confirm">Confirmar e gravar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
